import { supabase } from './supabase';

// Upload image to Supabase Storage
export async function saveProductImage(productId: number, imageData: string, isThumbnail: boolean = false, quality: number = 0.85): Promise<string> {
  try {
    console.log(`💾 Supabase保存開始: 商品ID=${productId}, ${isThumbnail ? 'サムネイル' : 'オリジナル'}画像`);
    console.log(`🎨 指定画質: ${Math.round(quality * 100)}%`);
    
    // Convert base64 to blob
    const base64Data = imageData.split(',')[1];
    if (!base64Data) {
      console.log('📄 URL画像: base64ではないためそのまま返却');
      return imageData; // Return original URL if not a base64 image
    }
    
    console.log(`🔄 圧縮処理開始: base64データを画質${Math.round(quality * 100)}%で圧縮中...`);
    
    // Create image blob with quality adjustment
    let blob: Blob;
    if (quality < 0.99) { // 99%未満の場合は圧縮処理を実行
      // Apply quality compression if needed
      try {
        const img = new Image();
        img.src = `data:image/jpeg;base64,${base64Data}`;
        
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });
        
        console.log(`📐 元画像サイズ: ${img.width}x${img.height}px`);
        
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        
        if (ctx) {
          // 高品質な描画設定
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          // 白背景を設定（透明背景を避けるため）
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          ctx.drawImage(img, 0, 0);
          blob = await new Promise(resolve => {
            canvas.toBlob(resolve as BlobCallback, 'image/jpeg', quality);
          }) as Blob;
          
          console.log(`🎨 Canvas圧縮完了: ${Math.round(quality * 100)}%画質で圧縮`);
          console.log(`📦 圧縮後サイズ: ${(blob.size / 1024).toFixed(2)}KB`);
        } else {
          throw new Error('Canvas context取得失敗');
        }
      } catch (error) {
        console.error('Canvas圧縮エラー、フォールバック処理:', error);
        blob = await fetch(`data:image/jpeg;base64,${base64Data}`).then(res => res.blob());
      }
    } else {
      console.log('🔄 無圧縮モード: 元画像をそのまま使用');
      blob = await fetch(`data:image/jpeg;base64,${base64Data}`).then(res => res.blob());
    }
    
    console.log(`📦 Blob作成完了: ${(blob.size / 1024).toFixed(2)}KB`);
    
    // Generate a unique filename with productId, timestamp, and random string
    const randomString = Math.random().toString(36).substring(2, 8);
    const timestamp = Date.now();
    const filename = `${productId}_${timestamp}_${randomString}.jpg`;
    const filePath = isThumbnail 
      ? `products/${productId}/thumbnails/${filename}`
      : `products/${productId}/originals/${filename}`;
    
    console.log(`📂 ファイルパス: ${filePath}`);
    
    // Upload to Supabase Storage
    console.log('☁️ Supabaseアップロード: ストレージへのアップロード開始...');
    const { data, error } = await supabase.storage
      .from('images')
      .upload(filePath, blob, {
        contentType: 'image/jpeg',
        upsert: false
      });
    
    if (error) throw error;
    console.log('✅ アップロード成功: Supabase Storageへの保存完了');
    
    // Get public URL
    const { data: urlData } = supabase.storage
      .from('images')
      .getPublicUrl(filePath);
    
    console.log(`🔗 公開URL生成: ${urlData.publicUrl}`);
    return urlData.publicUrl;
  } catch (error) {
    console.error('❌ Supabase保存失敗:', error);
    // Return the original image data if upload fails
    console.log('🔄 フォールバック: 元の画像データを返却');
    return imageData;
  }
}

// Get product images from Supabase Storage
export async function getProductImages(productId: number): Promise<{ originals: string[]; thumbnails: string[] }> {
  try {
    // List files in the originals folder
    const { data: originalsData, error: originalsError } = await supabase.storage
      .from('images')
      .list(`products/${productId}/originals`);
    
    if (originalsError) throw originalsError;
    
    // List files in the thumbnails folder
    const { data: thumbnailsData, error: thumbnailsError } = await supabase.storage
      .from('images')
      .list(`products/${productId}/thumbnails`);
    
    if (thumbnailsError) throw thumbnailsError;
    
    // Get public URLs for original images
    const originals = (originalsData || []).map(file => {
      const { data: urlData } = supabase.storage
        .from('images')
        .getPublicUrl(`products/${productId}/originals/${file.name}`);
      
      return urlData.publicUrl;
    });
    
    // Get public URLs for thumbnail images
    const thumbnails = (thumbnailsData || []).map(file => {
      const { data: urlData } = supabase.storage
        .from('images')
        .getPublicUrl(`products/${productId}/thumbnails/${file.name}`);
      
      return urlData.publicUrl;
    });
    
    return { originals, thumbnails };
  } catch (error) {
    console.error('Error getting product images:', error);
    return { originals: [], thumbnails: [] };
  }
}

// Delete product images from Supabase Storage
export async function deleteProductImages(productId: number): Promise<void> {
  try {
    // List files in the originals folder
    const { data: originalsData, error: originalsError } = await supabase.storage
      .from('images')
      .list(`products/${productId}/originals`);
    
    if (!originalsError && originalsData && originalsData.length > 0) {
      // Delete all original files
      const originalsToDelete = originalsData.map(file => `products/${productId}/originals/${file.name}`);
      
      const { error: deleteOriginalsError } = await supabase.storage
        .from('images')
        .remove(originalsToDelete);
      
      if (deleteOriginalsError) throw deleteOriginalsError;
    }
    
    // List files in the thumbnails folder
    const { data: thumbnailsData, error: thumbnailsError } = await supabase.storage
      .from('images')
      .list(`products/${productId}/thumbnails`);
    
    if (!thumbnailsError && thumbnailsData && thumbnailsData.length > 0) {
      // Delete all thumbnail files
      const thumbnailsToDelete = thumbnailsData.map(file => `products/${productId}/thumbnails/${file.name}`);
      
      const { error: deleteThumbnailsError } = await supabase.storage
        .from('images')
        .remove(thumbnailsToDelete);
      
      if (deleteThumbnailsError) throw deleteThumbnailsError;
    }
  } catch (error) {
    console.error('Error deleting product images:', error);
    // Don't throw error to prevent blocking product deletion
  }
}